/**
 * Helper centralizado para gerenciamento de chaves de pipeline do CorelDRAW.
 * 
 * Este módulo encapsula todo o acesso às chaves relacionadas à pipeline de preparação
 * de pedidos, garantindo consistência e facilitando manutenção futura.
 * 
 * CONTRATO DAS CHAVES:
 * 
 * 1. Flag de Pipeline Preparado:
 *    - Chave: `corel:pipeline:prepared:v1:{orcId}:{oitId}`
 *    - Valor: `pipelineId` (string)
 *    - Storage: sessionStorage (encapsulado)
 *    - Propósito: Indica que existe um snapshot preparado para o par (pedido, item)
 * 
 * 2. Snapshot de Objetos Organizados:
 *    - Chave: `cdr:corel:organizedObjects:{pipelineId}`
 *    - Valor: Envelope `{ meta: { docKey, capturedAt, total }, objetos: any[] }`
 *    - Storage: sessionStorage via `storage` utility (com TTL)
 *    - Propósito: Armazena o estado sincronizado dos objetos daquele pipeline
 * 
 * RELAÇÃO ENTRE AS CHAVES:
 * - `preparedFlag(orcId, oitId)` → retorna `pipelineId`
 * - `snapshotKey(pipelineId)` → retorna envelope com objetos
 */

import { storage } from '../../storage/storage';
import { Keys } from '../../storage/keys';
import { terminalLog, sendToTerminal } from '../../terminalLogger';

export interface PreparedSnapshotMeta {
  docKey?: string | null;
  capturedAt: number;
  total: number;
  openSessionId?: string | null;
  documentToken?: string | null;
}

export interface PreparedSnapshotEnvelope {
  meta: PreparedSnapshotMeta;
  objetos: any[];
}

// Guard para reduzir logs em produção
const isDevelopment = import.meta.env.MODE !== 'production';

/**
 * Gera a chave da flag de pipeline preparado para um par (pedido, item).
 */
export function getPreparedKey(orcId: number | string, oitId: number | string): string {
  return `corel:pipeline:prepared:v1:${orcId}:${oitId}`;
}

/**
 * Gera a chave do snapshot de objetos organizados para um pipelineId.
 */
export function getSnapshotKey(pipelineId: string): string {
  return `${Keys.corel.organizedObjects}:${pipelineId}`;
}

/**
 * Lê o pipelineId associado a um par (pedido, item) a partir da flag preparada.
 * Retorna null se não existir.
 */
export function readPreparedPipelineId(orcId: number | string, oitId: number | string): string | null {
  try {
    const key = getPreparedKey(orcId, oitId);
    const pipelineId = sessionStorage.getItem(key);
    if (pipelineId && isDevelopment) {
      terminalLog.debug('[PIPELINE_KEYS] readPreparedPipelineId', { orcId, oitId, pipelineId });
    }
    return pipelineId;
  } catch (error) {
    // Sempre logar erros, mesmo em produção
    terminalLog.error('[PIPELINE_KEYS] Erro ao ler preparedPipelineId', { 
      orcId, 
      oitId, 
      error: error instanceof Error ? error.message : String(error) 
    });
    return null;
  }
}

/**
 * Define a flag de pipeline preparado associando um pipelineId a um par (pedido, item).
 */
export function setPreparedPipelineId(orcId: number | string, oitId: number | string, pipelineId: string): void {
  try {
    const key = getPreparedKey(orcId, oitId);
    sessionStorage.setItem(key, pipelineId);
    if (isDevelopment) {
      terminalLog.debug('[PIPELINE_KEYS] setPreparedPipelineId', { orcId, oitId, pipelineId });
      // Flag preparada definida - silencioso
    }
  } catch (error) {
    // Sempre logar erros, mesmo em produção
    terminalLog.error('[PIPELINE_KEYS] Erro ao definir preparedPipelineId', { 
      orcId, 
      oitId, 
      pipelineId,
      error: error instanceof Error ? error.message : String(error) 
    });
    sendToTerminal('❌ [PIPELINE_KEYS] Erro ao definir flag preparada', { 
      orcId, 
      oitId, 
      pipelineId,
      error: error instanceof Error ? error.message : String(error) 
    });
  }
}

/**
 * Remove a flag de pipeline preparado para um par (pedido, item).
 * Não remove o snapshot associado (use clearPreparedPipeline para limpar tudo).
 */
export function clearPreparedFlag(orcId: number | string, oitId: number | string): void {
  try {
    const key = getPreparedKey(orcId, oitId);
    sessionStorage.removeItem(key);
    if (isDevelopment) {
      terminalLog.debug('[PIPELINE_KEYS] clearPreparedFlag', { orcId, oitId });
    }
  } catch (error) {
    // Sempre logar erros, mesmo em produção
    terminalLog.error('[PIPELINE_KEYS] Erro ao limpar preparedFlag', { 
      orcId, 
      oitId, 
      error: error instanceof Error ? error.message : String(error) 
    });
  }
}

/**
 * Limpa completamente a pipeline preparada para um par (pedido, item):
 * - Remove a flag de pipeline preparado
 * - Remove o snapshot associado (se existir)
 * 
 * Use esta função quando quiser garantir que não há resíduos de uma pipeline anterior.
 */
export function clearPreparedPipeline(orcId: number | string, oitId: number | string): void {
  try {
    const pipelineId = readPreparedPipelineId(orcId, oitId);
    if (pipelineId) {
      clearSnapshot(pipelineId);
      if (isDevelopment) {
        // Snapshot removido junto com flag - silencioso
      }
    }
    clearPreparedFlag(orcId, oitId);
    if (isDevelopment) {
      terminalLog.debug('[PIPELINE_KEYS] clearPreparedPipeline', { orcId, oitId, hadSnapshot: !!pipelineId });
      // Pipeline limpa completamente - silencioso
    }
  } catch (error) {
    // Sempre logar erros, mesmo em produção
    terminalLog.error('[PIPELINE_KEYS] Erro ao limpar preparedPipeline', { 
      orcId, 
      oitId, 
      error: error instanceof Error ? error.message : String(error) 
    });
  }
}

/**
 * Lê o snapshot de objetos organizados para um pipelineId.
 * Retorna null se não existir ou se o TTL tiver expirado.
 */
export function readSnapshot(pipelineId: string): PreparedSnapshotEnvelope | null {
  try {
    const key = getSnapshotKey(pipelineId);
    const envelope = storage.get<PreparedSnapshotEnvelope>(key, { area: 'session' });
    if (envelope && isDevelopment) {
      terminalLog.debug('[PIPELINE_KEYS] readSnapshot', { 
        pipelineId, 
        objetosCount: envelope.objetos?.length ?? 0,
        capturedAt: envelope.meta?.capturedAt 
      });
    }
    return envelope || null;
  } catch (error) {
    // Sempre logar erros, mesmo em produção
    terminalLog.error('[PIPELINE_KEYS] Erro ao ler snapshot', { 
      pipelineId, 
      error: error instanceof Error ? error.message : String(error) 
    });
    return null;
  }
}

/**
 * Grava o snapshot de objetos organizados para um pipelineId.
 * 
 * @param pipelineId - ID único do pipeline
 * @param envelope - Envelope contendo meta e objetos
 * @param ttlMs - Tempo de vida em milissegundos (padrão: 15s)
 */
export function writeSnapshot(
  pipelineId: string,
  envelope: PreparedSnapshotEnvelope,
  ttlMs: number = 15 * 1000
): void {
  // Validação: verificar estrutura do envelope
  if (!pipelineId || typeof pipelineId !== 'string' || pipelineId.trim().length === 0) {
    terminalLog.error('[PIPELINE_KEYS] Envelope inválido: pipelineId inválido', { pipelineId });
    return;
  }
  
  if (!envelope || typeof envelope !== 'object') {
    terminalLog.error('[PIPELINE_KEYS] Envelope inválido: envelope não é objeto', { pipelineId });
    return;
  }
  
  if (!Array.isArray(envelope.objetos)) {
    terminalLog.error('[PIPELINE_KEYS] Envelope inválido: objetos não é array', { 
      pipelineId, 
      objetosType: typeof envelope.objetos 
    });
    return;
  }
  
  if (!envelope.meta || typeof envelope.meta !== 'object') {
    terminalLog.error('[PIPELINE_KEYS] Envelope inválido: meta não é objeto', { pipelineId });
    return;
  }
  
  if (typeof envelope.meta.capturedAt !== 'number' || envelope.meta.capturedAt <= 0) {
    terminalLog.error('[PIPELINE_KEYS] Envelope inválido: capturedAt inválido', { 
      pipelineId, 
      capturedAt: envelope.meta.capturedAt 
    });
    return;
  }
  
  try {
    const key = getSnapshotKey(pipelineId);
    storage.set(key as any, envelope, { area: 'session', ttlMs });
    if (isDevelopment) {
      terminalLog.debug('[PIPELINE_KEYS] writeSnapshot', { 
        pipelineId, 
        objetosCount: envelope.objetos?.length ?? 0,
        ttlMs,
        capturedAt: envelope.meta?.capturedAt 
      });
      // Snapshot gravado - silencioso
    }
  } catch (error) {
    // Sempre logar erros, mesmo em produção
    terminalLog.error('[PIPELINE_KEYS] Erro ao gravar snapshot', { 
      pipelineId, 
      error: error instanceof Error ? error.message : String(error) 
    });
    sendToTerminal('❌ [PIPELINE_KEYS] Erro ao gravar snapshot', { 
      pipelineId, 
      error: error instanceof Error ? error.message : String(error) 
    });
  }
}

/**
 * Remove o snapshot de objetos organizados para um pipelineId.
 */
export function clearSnapshot(pipelineId: string): void {
  try {
    const key = getSnapshotKey(pipelineId);
    storage.remove(key as any, 'session');
    if (isDevelopment) {
      terminalLog.debug('[PIPELINE_KEYS] clearSnapshot', { pipelineId });
    }
  } catch (error) {
    // Sempre logar erros, mesmo em produção
    terminalLog.error('[PIPELINE_KEYS] Erro ao limpar snapshot', { 
      pipelineId, 
      error: error instanceof Error ? error.message : String(error) 
    });
  }
}

